/**
 * Builds the multi-sheet email/form XLSX workbook: a Summary sheet of header
 * rows, one sheet per ArTable snapshot, and the org logo on each.
 *
 * Consumed by the FormEmailModal XLSX path, which hands over snapshots produced
 * by `createArTableExportSnapshot` — their `visibleRowIds`, `isCellSuppressed`
 * and `getBundleHeaderCell` already carry the kit/group row, member-cell and
 * header-cell rules the grid applies (SYD-2279/2276).
 *
 * Only workbook assembly lives here. Each table sheet's column schema comes from
 * `buildArTableSheetSchema`, shared with `exportArTableToExcel`, so the two XLSX
 * writers cannot drift on column typing, width or time-range splitting. A
 * snapshot left with no visible column contributes an empty sheet; it never
 * costs the Summary sheet or the other tables their sheets.
 *
 * A row carrying an annotation prints it as a strip row directly beneath that
 * row, merged across the sheet's columns, mirroring the grid and the PDF rather
 * than adding a column the schema never declared (SYD-2970). A sheet with no
 * visible column prints no strip, keeping the empty-sheet rule above.
 */

import type { FieldType } from '#types/System.types';
import type {
  ArColumnDefinition,
  ArRowData,
  ArRowId,
} from '#types/System.Artifacts.types';
import type { ArFormulaEngineConfig } from '#types';
import { LOGO_EXPORT_SIZE_DEFAULT } from '#types/System.Parameters.types';
import type { DocEntry } from '@/store/Store.types';
import type { ImagePosition } from 'exceljs';
import type { XlsxLogoImage } from './fetchXlsxLogoImage';
import type { FormattedCellValue } from '@/components/CompositeField/formattedCellValue';
import { coerceArTableSheetCell } from '@/components/ArTable/export/coerceArTableSheetCell';
import { buildArTableSheetSchema } from '@/components/ArTable/export/buildArTableSheetSchema';
import type { BundleHeaderCellReader } from '@/components/ArTable/export/createBundleHeaderCellReader';

type ArTableExportSnapshot = {
  tableId: string;
  filenameLabel: string;
  sheetName: string;
  columns: readonly ArColumnDefinition[];
  visibleRowIds: readonly ArRowId[];
  rowsMap: Map<ArRowId, DocEntry<ArRowData>>;
  getColumnLabel: (column: ArColumnDefinition) => string;
  /**
   * True when the cell sits on a bundle member row whose column withholds it from
   * the active view. Suppressed cells yield no value at all, so they neither reach
   * the sheet nor influence column type detection, width, or time-range detection.
   */
  isCellSuppressed: (rowId: ArRowId, columnId: string) => boolean;
  /**
   * Resolves a bundle-header row's cell through the shared header resolver, or
   * reports the row is no header at all. Header cells bypass every generic read
   * below, including the formula overlay — the pre-SYD-2276 pass left header docs
   * carrying stale zeros and dotted `{fields}` objects no surface displays.
   */
  getBundleHeaderCell: BundleHeaderCellReader;
  /**
   * Names the formula columns and drives their evaluation inside
   * `buildArTableSheetSchema`. The snapshot carries no computed value of its own.
   */
  formulaConfig: ArFormulaEngineConfig;
  fieldTypes?: Record<string, FieldType>;
  formatCompositeValue?: (columnTypeId: string, cellValue: unknown) => string | number | FormattedCellValue | undefined;
};

type HeaderRow = {
  label: string;
  value: string;
};

type HeaderSchemaItem = {
  column: string;
  type: typeof String;
  value: (row: HeaderRow) => string;
  width: number;
  wrap?: boolean;
  headerStyle?: { fontWeight?: 'bold' | 'normal' };
  getCellStyle?: (row: HeaderRow) => { fontWeight?: 'bold' | 'normal' };
};

type WorkbookResult = {
  blob: Blob;
  filename: string;
};

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const sanitizeSheetName = (name: string): string => {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim();
  return cleaned.length > 31 ? cleaned.substring(0, 31) : cleaned;
};

const dedupeSheetNames = (names: string[]): string[] => {
  const seen = new Map<string, number>();
  return names.map((raw) => {
    const base = sanitizeSheetName(raw) || 'Sheet';
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    if (count === 1) return base;
    const suffix = `-${count}`;
    const trimmedBase = base.substring(0, Math.max(0, 31 - suffix.length));
    return `${trimmedBase}${suffix}`;
  });
};

const buildHeaderSheet = (rows: readonly HeaderRow[]) => {
  const schema: HeaderSchemaItem[] = [
    {
      column: '',
      type: String,
      value: (r) => r.label,
      width: 30,
      wrap: true,
      headerStyle: { fontWeight: 'normal' },
      getCellStyle: (r) => (r.label ? { fontWeight: 'bold' } : { fontWeight: 'normal' }),
    },
    {
      column: '',
      type: String,
      value: (r) => r.value,
      width: 80,
      wrap: true,
      headerStyle: { fontWeight: 'normal' },
    },
  ];

  return { rows, schema, sheetName: 'Summary' };
};

const buildArTableSheet = (snapshot: ArTableExportSnapshot) => ({
  ...buildArTableSheetSchema(snapshot),
  sheetName: snapshot.sheetName,
});

const LOGO_BASE_HEIGHT_PX = 60;
const MM_TO_PX = LOGO_BASE_HEIGHT_PX / LOGO_EXPORT_SIZE_DEFAULT;
const PX_TO_ROW_HEIGHT_PT = 72 / 96;
const LOGO_ROW_HEIGHT_PADDING_PT = 8;
/**
 * Empty row beneath the logo. Generous height (16pt) absorbs Windows DPI-scaling
 * variance (125%/150%) that can shrink effective row 1 height and cause the
 * fixed-pixel-height logo to overflow into row 2.
 */
const LOGO_BUFFER_ROW_HEIGHT_PT = 16;

/** One wrapped line of the annotation strip, in points. */
const ANNOTATION_LINE_HEIGHT_PT = 14;

/** Excel rejects a worksheet carrying a row taller than this, in points. */
const EXCEL_MAX_ROW_HEIGHT_PT = 409.5;

/**
 * Height an annotation strip needs once merged across `sheetWidth` characters,
 * capped at what Excel accepts — a long annotation on a narrow sheet scrolls
 * inside its cell rather than taking the whole file down with it.
 * Excel auto-fits a wrapped cell's row only while it is unmerged, so the merged
 * strip would show its first line alone unless the height is set here. Author-typed
 * line breaks count as their own lines.
 */
const annotationRowHeightPt = (annotation: string, sheetWidth: number): number => {
  const charsPerLine = Math.max(1, sheetWidth);
  const lines = annotation
    .split('\n')
    .reduce((total, line) => total + Math.max(1, Math.ceil(line.length / charsPerLine)), 0);
  return Math.min(EXCEL_MAX_ROW_HEIGHT_PT, lines * ANNOTATION_LINE_HEIGHT_PT);
};

async function createXlsxWorkbookBlob(params: {
  headerRows: readonly HeaderRow[];
  tableSnapshots: readonly ArTableExportSnapshot[];
  filename: string;
  logoImage?: XlsxLogoImage;
  logoExportSizeMm?: number;
}): Promise<WorkbookResult> {
  const header = buildHeaderSheet(params.headerRows);
  const tableSheets = params.tableSnapshots.map(buildArTableSheet);

  const sheets = dedupeSheetNames([header.sheetName, ...tableSheets.map((s) => s.sheetName)]);

  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  const imageId = params.logoImage
    ? workbook.addImage({ buffer: params.logoImage.buffer, extension: params.logoImage.extension })
    : undefined;

  const intrinsic = params.logoImage;

  const addLogoToSheet = (worksheet: ReturnType<typeof workbook.addWorksheet>): void => {
    if (imageId === undefined || intrinsic === undefined) return;
    if (intrinsic.widthPx === 0 || intrinsic.heightPx === 0) return;
    const logoHeightPx = (params.logoExportSizeMm ?? LOGO_EXPORT_SIZE_DEFAULT) * MM_TO_PX;
    const logoWidthPx = logoHeightPx * (intrinsic.widthPx / intrinsic.heightPx);
    const row1HeightPt = Math.ceil(logoHeightPx * PX_TO_ROW_HEIGHT_PT) + LOGO_ROW_HEIGHT_PADDING_PT;
    worksheet.addRow([]).height = row1HeightPt;
    worksheet.addRow([]).height = LOGO_BUFFER_ROW_HEIGHT_PT;

    const logoImagePosition: ImagePosition = {
      tl: { col: 0, row: 0 },
      ext: { width: logoWidthPx, height: logoHeightPx },
    };
    worksheet.addImage(imageId, logoImagePosition);
  };

  const summarySheetName = sheets[0] ?? 'Summary';
  const summary = workbook.addWorksheet(sanitizeSheetName(summarySheetName) || 'Summary');

  summary.getColumn(1).width = header.schema[0]?.width ?? 30;
  summary.getColumn(2).width = header.schema[1]?.width ?? 80;
  summary.getColumn(1).alignment = { wrapText: true, vertical: 'top' };
  summary.getColumn(2).alignment = { wrapText: true, vertical: 'top' };

  addLogoToSheet(summary);

  for (let i = 0; i < header.rows.length; i++) {
    const row = header.rows[i];
    const excelRow = summary.addRow([row.label, row.value]);
    if (row.label) {
      excelRow.getCell(1).font = { bold: true };
    }
  }

  for (let sheetIndex = 0; sheetIndex < tableSheets.length; sheetIndex++) {
    const table = tableSheets[sheetIndex];
    const worksheetName = sheets[sheetIndex + 1] ?? table.sheetName;
    const worksheet = workbook.addWorksheet(sanitizeSheetName(worksheetName) || `Sheet${sheetIndex + 2}`);

    addLogoToSheet(worksheet);

    worksheet.addRow(table.schema.map((col) => col.column));
    const headerRowNum = worksheet.rowCount;
    const headerRow = worksheet.getRow(headerRowNum);
    headerRow.font = { bold: true };
    headerRow.alignment = { wrapText: true, vertical: 'top' };

    for (let colIndex = 0; colIndex < table.schema.length; colIndex++) {
      const schemaItem = table.schema[colIndex];
      const column = worksheet.getColumn(colIndex + 1);
      column.width = schemaItem.width;
      column.alignment = { wrapText: schemaItem.wrap !== false, vertical: 'top' };
      if (schemaItem.type === Date) {
        column.numFmt = schemaItem.format ?? 'MM/DD/YYYY HH:mm:ss';
      }
    }

    /** Character width of the whole sheet: what one annotation strip spans once merged. */
    const sheetWidth = table.schema.reduce((total, schemaItem) => total + schemaItem.width, 0);

    for (const row of table.rows) {
      const cells = table.schema.map((schemaItem) =>
        coerceArTableSheetCell(schemaItem, schemaItem.value(row)));
      const addedRow = worksheet.addRow(cells.map((cell) => cell.value));
      cells.forEach((cell, index) => {
        if (cell.numFmt) addedRow.getCell(index + 1).numFmt = cell.numFmt;
      });

      /**
       * A sheet left with no visible column has no span for a strip to merge
       * across, so it prints none: annotations follow the columns they annotate,
       * and must never become the sole content of a customer-facing sheet.
       */
      const annotation = table.schema.length > 0 ? row.entry.data?.annotation?.trim() : undefined;
      if (annotation) {
        const annotationRow = worksheet.addRow([annotation]);
        const annotationCell = annotationRow.getCell(1);
        annotationCell.alignment = { wrapText: true, vertical: 'top', indent: 1 };
        annotationCell.font = { italic: true };
        if (table.schema.length > 1) {
          worksheet.mergeCells(annotationRow.number, 1, annotationRow.number, table.schema.length);
        }
        annotationRow.height = annotationRowHeightPt(annotation, sheetWidth);
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as ArrayBuffer], { type: EXCEL_MIME_TYPE });
  return { blob, filename: params.filename };
}

export default createXlsxWorkbookBlob;
export type { HeaderRow, WorkbookResult, ArTableExportSnapshot };

